/**
 * Sales-order header amendment gates (LT-004).
 *
 * Mirrors `edit_sales_order` after migration 0125: header fields may be
 * amended until postage/dispatch is committed. Line edits stay on the
 * stricter `edit_sales_order_lines` contract (blocked once fulfilled).
 */

import { addressFromJson, type AddressValues } from "@/components/feature/AddressFields";
import { isUsableCanonicalAddress } from "@/lib/providers/order-addresses";

export const SO_HEADER_EDIT_TERMINAL: string[] = [
  "completed",
  "cancelled",
  "returned",
  "partially_returned",
];

export const SO_LINE_EDIT_BLOCKED: string[] = [
  "fulfilled",
  "completed",
  "cancelled",
  "returned",
  "partially_returned",
];

export const SHIPMENT_DISPATCH_STATUSES: string[] = [
  "shipped",
  "in_transit",
  "delivered",
  "pending_carrier",
];

/** Signals the warehouse UI loads to decide whether postage/dispatch is committed. */
export interface ShipmentCommitSignals {
  status: string;
  carrier_reference?: string | null;
  idempotency_key?: string | null;
  tracking_status?: string | null;
}

export function isShipmentDispatchCommitted(s: ShipmentCommitSignals): boolean {
  if (SHIPMENT_DISPATCH_STATUSES.includes(s.status)) return true;
  if (s.carrier_reference != null && String(s.carrier_reference).trim() !== "") return true;
  if (s.idempotency_key != null && String(s.idempotency_key).trim() !== "") return true;
  if (s.tracking_status != null && String(s.tracking_status).trim() !== "") return true;
  return false;
}

export function canEditSalesOrderHeader(
  status: string | null | undefined,
  shipments: ShipmentCommitSignals[] = [],
): boolean {
  if (!status) return false;
  if (SO_HEADER_EDIT_TERMINAL.includes(status)) return false;
  if (status === "fulfilled") {
    return shipments
      .filter((s) => s.status !== "cancelled")
      .every((s) => !isShipmentDispatchCommitted(s));
  }
  return true;
}

export function canEditSalesOrderLines(status: string | null | undefined): boolean {
  if (!status) return false;
  return !SO_LINE_EDIT_BLOCKED.includes(status);
}

export function shouldShowEditOrderCta(
  canManage: boolean,
  status: string | null | undefined,
  shipments: ShipmentCommitSignals[] = [],
): boolean {
  return canManage && canEditSalesOrderHeader(status, shipments);
}

/**
 * Ship To is the order's customer delivery address.
 * shipment.ship_to and customer master are ignored so sender/location cannot masquerade.
 */
export function resolveShipmentDestination(
  _shipTo: Record<string, unknown> | null | undefined,
  orderSnapshot: Record<string, unknown> | null | undefined,
  _customerShipping: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  void _shipTo;
  void _customerShipping;
  if (isUsableCanonicalAddress(orderSnapshot)) return orderSnapshot as Record<string, unknown>;
  return null;
}

export const POST_PICK_HEADER_EDIT_WARNING =
  "Inventory has already been picked and packed. Quantity, price, and line changes are locked. You can still update the delivery address and order notes until a label or dispatch is committed.";

export interface SalesOrderEditFormValues {
  shipping: AddressValues;
  billing: AddressValues;
  priority: string;
  customerReference: string;
  notes: string;
}

export function valuesFromOrder(so: {
  shipping_address: Record<string, string> | null;
  billing_address: Record<string, string> | null;
  priority: string | null;
  customer_reference: string | null;
  notes: string | null;
}): SalesOrderEditFormValues {
  return {
    shipping: addressFromJson(so.shipping_address),
    billing: addressFromJson(so.billing_address),
    priority: so.priority || "normal",
    customerReference: so.customer_reference ?? "",
    notes: so.notes ?? "",
  };
}
